// 系统设置 / 账号 / 审计的状态文案与颜色（文档 18 §4：5 色 + 灰，一行只一个实底标签；次级状态用色点）。
import type { TagTone } from '@/lib/status';

/* ------------------------------ 账号 / 角色 ------------------------------ */

export const activeState = (on: boolean) => (on ? { label: '启用', tone: 'green' as TagTone } : { label: '停用', tone: 'none' as TagTone });

export const roleKind = (preset: boolean) => (preset ? { label: '预置', tone: 'blue' as TagTone } : { label: '自定义', tone: 'violet' as TagTone });

export const baseRoleLabel: Record<string, string> = {
  ADMIN: '管理员', SALES: '业务员', FOLLOWER: '跟单', WAREHOUSE: '仓库', PURCHASE: '采购', VIEWER: '只读',
};

/* ------------------------------ 物流公司 ------------------------------ */

export const carrierType: Record<string, string> = { EXPRESS: '快递', LOGISTICS: '物流' };
export const carrierSource = (v?: string | null) => (v === 'preset' ? '预置' : v === 'v2-carrier-import' ? '批量导入' : '手工');

/* ------------------------------ 导入导出 ------------------------------ */

export const importType: Record<string, string> = { INITIAL_STOCK: '期初库存', DATA_EXPORT: '数据导出', CARRIER_COMPANY: '物流公司' };

export function importStatus(type: string, status: string): { label: string; tone: TagTone } {
  if (type === 'DATA_EXPORT') return status === 'FAILED' ? { label: '导出失败', tone: 'red' } : { label: '已完成', tone: 'green' };
  if (status === 'SUCCESS') return { label: '全部成功', tone: 'green' };
  if (status === 'PARTIAL') return { label: '部分失败', tone: 'orange' };
  return { label: '全部失败', tone: 'red' };
}

/** 老接口的 mode：add = 追加，其余 = 覆盖（REPO/src/app/(app)/imports/page.tsx） */
export const importMode = (m?: string | null) => (m ? (m === 'add' ? '追加' : m === 'skip' ? '跳过同名' : '覆盖') : null);

/* ------------------------------ 操作日志 ------------------------------ */

/** 动作中文名：老系统 audit-logs/page.tsx 的 actionLabels + 之后新增的动作（grep writeAuditLog 全量） */
export const auditAction: Record<string, string> = {
  'order.create': '新建订单', 'order.update': '修改订单', 'order.submit': '提交预占', 'order.approve': '审核并分配',
  'order.cancel': '取消订单', 'order.delete': '删除订单', 'order.close': '部分履约结案', 'order.status.update': '更新订单状态',
  'order.invoice.update': '更新开票', 'order.change.request': '申请修改', 'order.change.approve': '审核通过变更',
  'order.change.reject': '驳回变更', 'order.change.acknowledge': '确认知晓变更',
  'order.exception.create': '登记异常', 'order.exception.update': '更新异常', 'order.exception.delete': '删除异常',
  'order.attachment.add': '上传订单附件',
  'order.exception.evidence.add': '添加异常凭证',
  'order.exception.reship': '异常补发', 'order.exception.return_inbound': '退货入库',
  'shipment.create': '新增发货',
  'inventory.adjust': '库存调整',
  'purchase.create': '新建采购单', 'purchase.update': '修改采购单', 'purchase.receive': '采购转入库', 'purchase.reject': '到货拒收', 'purchase.return': '采购退货', 'purchase.cancel': '取消采购单',
  'purchase.delete': '删除采购单', 'purchase.closeRemaining': '关闭采购剩余',
  'settlement.customerReceipt': '登记客户收款', 'settlement.supplierPayment': '登记供应商付款',
  'settlement.customerInvoice': '登记客户开票', 'settlement.supplierInvoice': '登记供应商收票',
  'taobao.order.create': '淘宝店开单', 'taobao.order.update': '修改淘宝店订单', 'taobao.order.amount.correct': '修正淘宝店订单金额', 'taobao.order.complete': '淘宝店出库',
  'taobao.order.return': '淘宝店退货', 'taobao.order.delete': '删除淘宝店草稿', 'taobao.customer.create': '淘宝店新建客户',
  'product.create': '新建产品', 'product.update': '编辑产品', 'product.delete': '删除产品',
  'productCategory.create': '新建产品分类', 'productCategory.update': '修改产品分类', 'productCategory.delete': '删除产品分类', 'productCategory.reorder': '调整分类顺序',
  'bundle.create': '新建套装', 'bundle.update': '修改套装', 'bundle.delete': '删除套装',
  'customer.create': '新建客户', 'customer.update': '修改客户', 'customer.delete': '删除客户',
  'warehouse.create': '新建仓库', 'warehouse.update': '修改仓库', 'warehouse.delete': '删除仓库', 'warehouse.reorder': '调整仓库顺序',
  'warehouse.default_inbound.update': '设置默认入库仓',
  'carrierCompany.create': '新建物流公司', 'carrierCompany.update': '修改物流公司', 'carrierCompany.delete': '删除物流公司',
  'carrierCompany.import': '导入物流公司', 'carrierCompany.reorder': '调整物流公司顺序',
  'shop.create': '新建店铺', 'shop.update': '店铺改名', 'shop.delete': '删除店铺', 'shop.setDefault': '设置默认店铺',
  'settings.update': '修改系统设置',
  'assembly.line.create': '新增组装线', 'assembly.line.update': '修改组装线', 'assembly.line.delete': '删除组装线',
  'assembly.worker.create': '新增组装工人', 'assembly.worker.update': '修改组装工人', 'assembly.worker.delete': '删除组装工人',
  ROLE_PERMISSIONS_UPDATE: '修改角色权限',
  'auditLog.delete': '清理日志',
};
export const actionLabel = (a: string) => auditAction[a] || a;

/** 模块（日志行上的灰字标签）：由 entityType 归类 */
const ENTITY_MODULE: Record<string, string> = {
  SalesOrder: '订单', OrderChangeRequest: '订单', OrderException: '订单', Shipment: '订单', TaobaoOrder: '订单', TaobaoOrderReturn: '订单',
  InventoryTransaction: '库存', InventoryCountTask: '库存', PurchaseOrder: '采购',
  SettlementCashRecord: '结算', SettlementInvoiceRecord: '结算', SettlementStatement: '结算',
  Customer: '资料', Product: '资料', ProductBundle: '资料', ProductCategory: '资料',
  Warehouse: '系统', CarrierCompany: '系统', Shop: '系统', SystemSetting: '系统', AssemblyLine: '系统', AssemblyWorker: '系统', AuditLog: '系统', RoleDefinition: '系统',
};
export const entityModule = (t: string) => ENTITY_MODULE[t] || '其他';

/**
 * 筛选 chips：老接口只支持 entityType 精确匹配（一次一个），所以 chips 按对象类型列，不能按「模块」合并。
 * 模块级筛选、计数、时间范围、操作人筛选都要后端补参数（见报告）。
 */
export const ENTITY_FILTERS: { key: string; label: string }[] = [
  { key: 'SalesOrder', label: '订单' },
  { key: 'OrderChangeRequest', label: '变更单' },
  { key: 'OrderException', label: '异常' },
  { key: 'Shipment', label: '发货' },
  { key: 'InventoryTransaction', label: '库存' },
  { key: 'PurchaseOrder', label: '采购' },
  { key: 'SettlementCashRecord', label: '收付款' },
  { key: 'SettlementInvoiceRecord', label: '发票' },
  { key: 'TaobaoOrder', label: '淘宝店' },
  { key: 'TaobaoOrderReturn', label: '淘宝店退货' },
  { key: 'Customer', label: '客户' },
  { key: 'Product', label: '产品' },
  { key: 'ProductBundle', label: '套装' },
  { key: 'ProductCategory', label: '产品分类' },
  { key: 'Warehouse', label: '仓库' },
  { key: 'CarrierCompany', label: '物流公司' },
  { key: 'AssemblyLine', label: '组装线' },
  { key: 'AssemblyWorker', label: '组装工人' },
  { key: 'AuditLog', label: '日志清理' },
];
